import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../../types/navigation';
import { useAuth } from '../../hooks/useAuth';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../hooks/useTheme';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import { SubscriptionTierBadge } from '../../components/shared/SubscriptionTierBadge';
import { subscriptionAPI, SubscriptionStatus } from '../../api/subscriptions';
import { SubscriptionPlan } from '../../types/auth';
import { useFeatureFlag, isFeatureEnabled } from '../../hooks/useFeatureFlags';

interface BillingSectionProps {
  title: string;
  children: React.ReactNode;
}

const BillingSection = ({ title, children }: BillingSectionProps) => {
  const { colors } = useTheme();

  return (
    <View style={styles.section}>
      <Text style={[styles.sectionTitle, { color: colors.primary }]}>
        {title}
      </Text>
      <View style={styles.sectionContent}>{children}</View>
    </View>
  );
};

interface BillingItemProps {
  icon: string;
  title: string;
  subtitle?: string;
  onPress?: () => void;
  rightElement?: React.ReactNode;
  showChevron?: boolean;
}

const BillingItem = ({
  icon,
  title,
  subtitle,
  onPress,
  rightElement,
  showChevron = true,
}: BillingItemProps) => {
  const { colors } = useTheme();

  return (
    <TouchableOpacity
      style={[
        styles.billingItem,
        { backgroundColor: colors.card, borderColor: colors.border },
      ]}
      onPress={onPress}
      disabled={!onPress}
    >
      <View style={styles.billingContent}>
        <Ionicons
          name={icon as keyof typeof Ionicons.glyphMap}
          size={22}
          color={colors.primary}
        />
        <View style={styles.billingTextContainer}>
          <Text style={[styles.billingText, { color: colors.text }]}>
            {title}
          </Text>
          {subtitle && (
            <Text style={[styles.billingSubtitle, { color: colors.secondary }]}>
              {subtitle}
            </Text>
          )}
        </View>
      </View>
      {rightElement ||
        (onPress && showChevron && (
          <Ionicons name="chevron-forward" size={20} color={colors.secondary} />
        ))}
    </TouchableOpacity>
  );
};

const BillingScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { subscriptionPlan, refreshUser } = useAuth();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const { data: pricingFlag, isLoading: isLoadingFlag } = useFeatureFlag(
    'pricing_subscription'
  );

  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);

  const loadSubscriptionStatus = useCallback(async () => {
    try {
      setIsLoadingStatus(true);
      const status = await subscriptionAPI.getStatus();
      setSubscriptionStatus(status);
    } catch {
      // Load failed
    } finally {
      setIsLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    void loadSubscriptionStatus();
  }, [loadSubscriptionStatus]);

  // Redirect if feature flag is enabled (hide billing when flag is enabled)
  useEffect(() => {
    if (
      !isLoadingFlag &&
      pricingFlag &&
      isFeatureEnabled(pricingFlag, 'pricing_subscription')
    ) {
      // Feature flag is enabled, hide billing - go back
      navigation.goBack();
    }
  }, [pricingFlag, isLoadingFlag, navigation]);

  const handleDowngradeToFree = async () => {
    Alert.alert(
      'Downgrade to Free',
      'Are you sure you want to downgrade to the free plan? You will lose access to premium features.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Downgrade',
          style: 'destructive',
          onPress: async () => {
            try {
              setIsLoading(true);
              await subscriptionAPI.downgradeToFree();
              await refreshUser();
              Alert.alert('Success', 'Successfully downgraded to Free Plan');
            } catch (error: unknown) {
              Alert.alert(
                'Error',
                error instanceof Error
                  ? error.message
                  : 'Failed to downgrade subscription'
              );
            } finally {
              setIsLoading(false);
            }
          },
        },
      ]
    );
  };

  const getPlanFeatures = (plan: SubscriptionPlan) => {
    switch (plan) {
      case SubscriptionPlan.FREE:
        return [
          '5 color analysis results',
          'Basic mood boards',
          'Limited wardrobe items',
          'Community access (limited)',
          'Daily drape test',
        ];
      case SubscriptionPlan.FREEMIUM_TRIAL:
        return [
          'All Pro features for 7 days',
          'Unlimited color analysis',
          'Unlimited mood boards',
          'Full wardrobe access',
          'AI outfit suggestions',
          'Priority support',
        ];
      case SubscriptionPlan.PRO:
        return [
          'Unlimited color analysis',
          'Unlimited mood boards',
          'Full wardrobe access',
          'AI outfit suggestions',
          'Professional templates',
          'Priority support',
          'Early access features',
        ];
      case SubscriptionPlan.PAY_PER_SCAN:
        return [
          'One-time deep color analysis',
          'Detailed color report',
          'Personalized recommendations',
          'Export results',
        ];
      default:
        return [];
    }
  };

  const getPlanPrice = (plan: SubscriptionPlan) => {
    switch (plan) {
      case SubscriptionPlan.FREE:
        return 'Free';
      case SubscriptionPlan.FREEMIUM_TRIAL:
        return 'Free for 7 days';
      case SubscriptionPlan.PRO:
        return '$9.99/month';
      case SubscriptionPlan.PAY_PER_SCAN:
        return '$2.99 per scan';
      default:
        return 'Free';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Show loading while checking feature flag or subscription status
  if (isLoadingFlag || isLoadingStatus) {
    return (
      <ResponsiveContainer style={{ backgroundColor: colors.background }}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={[styles.loadingText, { color: colors.text }]}>
            Loading subscription details...
          </Text>
        </View>
      </ResponsiveContainer>
    );
  }

  // Hide screen if feature flag is enabled
  if (isFeatureEnabled(pricingFlag, 'pricing_subscription')) {
    return null;
  }

  return (
    <ResponsiveContainer style={{ backgroundColor: colors.background }}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={{
          paddingBottom: 40 + insets.bottom,
          paddingHorizontal: 16,
          paddingTop: 16,
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Current Plan */}
        <BillingSection title="CURRENT PLAN">
          <TouchableOpacity
            style={[
              styles.currentPlanCard,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
            onPress={() => navigation.navigate('Pricing')}
          >
            <View style={styles.planHeader}>
              <SubscriptionTierBadge
                subscriptionPlan={subscriptionPlan}
                showUpgradeButton={false}
              />
              <Text style={[styles.planPrice, { color: colors.primary }]}>
                {getPlanPrice(subscriptionPlan)}
              </Text>
            </View>

            {subscriptionStatus?.is_on_trial &&
              subscriptionStatus?.trial_expires_at && (
                <View style={styles.trialInfo}>
                  <Ionicons
                    name="time-outline"
                    size={16}
                    color={colors.subscription}
                  />
                  <Text
                    style={[styles.trialText, { color: colors.subscription }]}
                  >
                    Trial expires:{' '}
                    {formatDate(subscriptionStatus.trial_expires_at)}
                  </Text>
                </View>
              )}

            {subscriptionStatus?.subscription_expires_at && (
              <View style={styles.expiryInfo}>
                <Ionicons
                  name="calendar-outline"
                  size={16}
                  color={colors.secondary}
                />
                <Text style={[styles.expiryText, { color: colors.secondary }]}>
                  Next billing:{' '}
                  {formatDate(subscriptionStatus.subscription_expires_at)}
                </Text>
              </View>
            )}

            <View style={styles.changePlanHint}>
              <Ionicons
                name="chevron-forward"
                size={16}
                color={colors.secondary}
              />
              <Text
                style={[styles.changePlanText, { color: colors.secondary }]}
              >
                Tap to change plan
              </Text>
            </View>
          </TouchableOpacity>
        </BillingSection>

        {/* Plan Features */}
        <BillingSection title="YOUR PLAN FEATURES">
          <View
            style={[
              styles.featuresCard,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
          >
            {getPlanFeatures(subscriptionPlan).map((feature, index) => (
              <View key={index} style={styles.featureItem}>
                <Ionicons
                  name="checkmark-circle"
                  size={16}
                  color={colors.success}
                />
                <Text style={[styles.featureText, { color: colors.text }]}>
                  {feature}
                </Text>
              </View>
            ))}
          </View>
        </BillingSection>

        {/* Subscription Management */}
        <BillingSection title="SUBSCRIPTION MANAGEMENT">
          <BillingItem
            icon="card-outline"
            title="Change Subscription Plan"
            subtitle="View all plans and pricing options"
            onPress={() => navigation.navigate('Pricing')}
          />

          {subscriptionPlan === SubscriptionPlan.PRO && (
            <BillingItem
              icon="card-outline"
              title="Manage Billing"
              subtitle="Update payment method or billing info"
              onPress={() =>
                Alert.alert('Info', 'Billing management coming soon')
              }
            />
          )}

          {subscriptionPlan !== SubscriptionPlan.FREE && (
            <BillingItem
              icon="close-circle-outline"
              title="Cancel Subscription"
              subtitle="Return to free plan"
              onPress={handleDowngradeToFree}
            />
          )}
        </BillingSection>

        {/* Billing History */}
        <BillingSection title="BILLING HISTORY">
          <View
            style={[
              styles.billingHistoryCard,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
          >
            <Text style={[styles.noHistoryText, { color: colors.secondary }]}>
              No billing history available
            </Text>
            <Text
              style={[styles.noHistorySubtext, { color: colors.secondary }]}
            >
              Your billing history will appear here once you make a purchase
            </Text>
          </View>
        </BillingSection>

        {/* Support */}
        <BillingSection title="SUPPORT">
          <BillingItem
            icon="help-circle-outline"
            title="Billing Support"
            subtitle="Get help with subscription issues"
            onPress={() => navigation.navigate('BillingSupport')}
          />
        </BillingSection>

        {/* Loading Overlay */}
        {isLoading && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={[styles.loadingText, { color: colors.text }]}>
              Processing...
            </Text>
          </View>
        )}
      </ScrollView>
    </ResponsiveContainer>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
  },
  section: {
    marginBottom: 18,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 6,
    marginLeft: 8,
  },
  sectionContent: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  currentPlanCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  planHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  planPrice: {
    fontSize: 18,
    fontWeight: '600',
  },
  trialInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  trialText: {
    marginLeft: 8,
    fontSize: 14,
  },
  expiryInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  expiryText: {
    marginLeft: 8,
    fontSize: 14,
  },
  featuresCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  featureText: {
    marginLeft: 8,
    fontSize: 14,
  },
  billingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
  },
  billingContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  billingTextContainer: {
    marginLeft: 12,
    flex: 1,
  },
  billingText: {
    fontSize: 16,
    fontWeight: '500',
  },
  billingSubtitle: {
    fontSize: 14,
    marginTop: 2,
  },
  billingHistoryCard: {
    padding: 24,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
  },
  noHistoryText: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 8,
  },
  noHistorySubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  changePlanHint: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: 12,
  },
  changePlanText: {
    fontSize: 12,
    marginLeft: 4,
  },
});

export default BillingScreen;
